import styled, { css } from "styled-components";

/**
 * Wrapper do seletor de datas
 * Container com posição relativa para o popover.
 */
export const DateRangePickerWrapper = styled.div`
  position: relative;
`;

/**
 * Botão gatilho do seletor
 */
export const TriggerButton = styled.button<{ $variant?: "default" | "bar" }>`
  width: 100%;
  min-height: 48px;
  padding: 0 ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.gray};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.mediumGray};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.md};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease-in-out;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.mediumGray};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primaryColor};
    outline-offset: 2px;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.lightGray};
    cursor: not-allowed;
    opacity: 0.45;
  }

  ${({ $variant }) =>
    $variant === "bar" &&
    css`
      border: none;
      background-color: transparent;
      color: ${({ theme }) => theme.colors.darkGray};
      padding: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-height: auto;
      font-size: ${({ theme }) => theme.fontSizes.md};

      &:hover:not(:disabled) {
        border-color: transparent;
      }

      &:disabled {
        background-color: transparent;
        opacity: 0.45;
      }
    `}
`;

/**
 * Placeholder do botão
 */
export const TriggerPlaceholder = styled.span<{ $variant?: "default" | "bar" }>`
  color: ${({ theme, $variant }) =>
    $variant === "bar" ? theme.colors.darkGray : theme.colors.placeholderGray};
`;

/**
 * Popover do seletor (Desktop)
 */
export const Popover = styled.div<{
  $variant?: "default" | "bar";
  $align?: "start" | "end";
}>`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.spacing(1)});
  ${({ $variant, $align, theme }) => {
    if ($variant === "bar" || !$align) {
      return css`
        left: 0;
      `;
    }
    const centered = `calc(100% + ${theme.spacing(2)} - ${theme.spacing(56)})`;
    return $align === "start"
      ? css`
          left: ${centered};
        `
      : css`
          right: ${centered};
        `;
  }}
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.spacing(3)};
  box-shadow:
    0 0 0 1px ${({ theme }) => theme.colors.gray},
    0 18px 44px rgba(23, 65, 90, 0.22);
  z-index: ${({ $variant }) => ($variant === "bar" ? 1000 : 10)};
  min-width: ${({ theme }) => theme.spacing(112)};
`;

/**
 * Container dos meses, com um divisor entre eles
 */
export const CalendarsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  & > * + * {
    border-left: 1px solid ${({ theme }) => theme.colors.dividerGray};
  }
`;

/**
 * Mês individual
 */
export const CalendarMonthContainer = styled.div`
  min-width: ${({ theme }) => theme.spacing(54)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)}
    ${({ theme }) => theme.spacing(2)};

  & [role="grid"] {
    padding: ${({ theme }) => theme.spacing(1)} 0 0;
  }
`;

/**
 * Cabeçalho do mês: seta, título centralizado e seta
 */
export const MonthHeader = styled.div`
  display: grid;
  grid-template-columns: 2.25rem 1fr 2.25rem;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

export const MonthTitle = styled.span`
  color: ${({ theme }) => theme.colors.deepDarkBlue};
  font-size: 1.125rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-align: center;
  text-transform: capitalize;
`;

export const MonthNavButton = styled.button`
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid ${({ theme }) => theme.colors.gray};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.deepDarkBlue};
  cursor: pointer;
  transition:
    background-color 0.2s ease-in-out,
    border-color 0.2s ease-in-out;

  & svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.mediumBlue};
    background-color: ${({ theme }) => theme.colors.lightBlue};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primaryColor};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

/**
 * Rodapé: resumo da seleção à esquerda e ações à direita
 */
export const PopoverFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2.5)}
    ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.colors.dividerGray};
`;

export const SelectionSummary = styled.p`
  min-width: 0;
  margin: 0;
  color: ${({ theme }) => theme.colors.deepDarkBlue};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

export const FooterActions = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const FooterButton = styled.button`
  min-height: 2.75rem;
  padding: 0 ${({ theme }) => theme.spacing(3)};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primaryColor};
    outline-offset: 2px;
  }
`;

export const ClearButton = styled(FooterButton)`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.darkBlue};

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightBlue};
  }
`;

export const ConfirmButton = styled(FooterButton)`
  padding: 0 ${({ theme }) => theme.spacing(4)};
  background-color: ${({ theme }) => theme.colors.yellow};
  color: ${({ theme }) => theme.colors.deepDarkBlue};

  &:hover {
    filter: brightness(0.95);
  }
`;
